import React from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Chip,
    Stack,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Divider,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {AccountBalance, Person, EventBusy, Percent, ManageAccounts} from '@mui/icons-material';

interface MaliHesaplarTabProps {
    hesaplar: any[];
    hesapPage: number;
    hesapRowsPerPage: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onNewHesap: () => void;
    onEditHesap: (row: any) => void;
    onRowClick: (row: any) => void;
}

const MaliHesaplarTab: React.FC<MaliHesaplarTabProps> = ({
                                                             hesaplar,
                                                             hesapPage,
                                                             hesapRowsPerPage,
                                                             onPageChange,
                                                             onRowsPerPageChange,
                                                             onNewHesap,
                                                             onEditHesap,
                                                             onRowClick,
                                                         }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const paginatedHesaplar = hesaplar.slice(hesapPage * hesapRowsPerPage, hesapPage * hesapRowsPerPage + hesapRowsPerPage);

    return (
        <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '12px', px: 4, fontWeight: 800 }}
                    startIcon={<AddIcon />}
                    onClick={onNewHesap}
                >
                    Yeni Hesap
                </Button>
            </Box>

            {isMobile ? (
                <Stack spacing={2}>
                    {paginatedHesaplar.map((row: any) => (
                        <Card 
                            key={row.id} 
                            onClick={() => onRowClick(row)}
                            sx={{ 
                                borderRadius: '16px', 
                                border: '1px solid rgba(0,0,0,0.05)',
                                opacity: row._rowIsActive ? 1 : 0.6,
                                transition: 'transform 0.2s',
                                '&:active': { transform: 'scale(0.98)' }
                            }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <AccountBalance color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight={800}>{row.hesapAdi}</Typography>
                                    </Stack>
                                    <Chip 
                                        label={row.hesapTipi} 
                                        size="small" 
                                        variant="outlined" 
                                        color={row.hesapTipi === 'İşletme' ? 'primary' : 'secondary'}
                                        sx={{ fontWeight: 700, borderRadius: '6px' }}
                                    />
                                </Box>
                                
                                <Divider sx={{ my: 1.5, opacity: 0.5 }} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Sahibi</Typography>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Person fontSize="inherit" sx={{ opacity: 0.6 }} />
                                            <Typography variant="body2" fontWeight={600}>{row.sahibi || '-'}</Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Komisyon</Typography>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Percent fontSize="inherit" sx={{ opacity: 0.6 }} />
                                            <Typography variant="body2" fontWeight={600}>%{row.komisyon}</Typography>
                                        </Stack>
                                    </Grid>
                                    {row.aktif_bitis && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="textSecondary" display="block">Bitiş Tarihi</Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <EventBusy fontSize="inherit" color="error" sx={{ opacity: 0.8 }} />
                                                <Typography variant="body2" fontWeight={600}>{row.aktif_bitis}</Typography>
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                                
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <IconButton 
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); onEditHesap(row); }}
                                        sx={{ bgcolor: 'grey.100' }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 800, py: 2 }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Hesap Adı</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Tip</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Sahibi</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Bitiş Tarihi</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>Komisyon (%)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedHesaplar.map((row: any) => (
                                <TableRow
                                    key={row.id}
                                    onClick={() => onRowClick(row)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'primary.light', '& .MuiTableCell-root': { color: 'primary.dark' } },
                                        opacity: row._rowIsActive ? 1 : 0.6,
                                        fontStyle: row._rowIsActive ? 'normal' : 'italic',
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.hesapAdi}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={row.hesapTipi} 
                                            size="small" 
                                            sx={{ fontWeight: 700, borderRadius: '6px' }} 
                                            color={row.hesapTipi === 'İşletme' ? 'primary' : 'default'} 
                                        />
                                    </TableCell>
                                    <TableCell>{row.sahibi}</TableCell>
                                    <TableCell>{row.aktif_bitis}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800 }}>{row.komisyon}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEditHesap(row);
                                            }}
                                            sx={{ '&:hover': { color: 'primary.main' } }}
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={hesaplar.length}
                rowsPerPage={hesapRowsPerPage}
                page={hesapPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                sx={{ mt: 1 }}
            />
        </>
    );
};

export default MaliHesaplarTab;
